import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { CrmPresence } from "../api/client.ts";
import { formatDate, formatNumber } from "../utils/format.ts";
import HubSpotLogo from "./HubSpotLogo.tsx";

const TOUCH_VERB: Record<string, string> = {
  call: "Called",
  email: "Emailed",
  meeting: "Met",
  linkedin: "Messaged",
};

type ChipColor = "default" | "success" | "error" | "info" | "warning";

/** The one thing worth saying about the tenant's CRM history, and its colour. */
export function crmPresenceLabel(crm: CrmPresence): { label: string; color: ChipColor } {
  if (crm.open_deals > 0) {
    return { label: crm.latest_deal_stage ? `Deal · ${crm.latest_deal_stage}` : "Open deal", color: "success" };
  }
  if (crm.latest_deal_outcome === "won") return { label: "Deal won", color: "success" };
  if (crm.lead_state === "UNQUALIFIED") {
    return { label: crm.lead_stage ?? "Disqualified", color: "error" };
  }
  if (crm.last_touched_at && crm.last_touch_channel) {
    const verb = TOUCH_VERB[crm.last_touch_channel] ?? "Touched";
    return { label: `${verb} ${formatDate(crm.last_touched_at)}`, color: "info" };
  }
  return { label: "In HubSpot", color: "default" };
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

/** Everything behind the chip, one line each. */
export function CrmPresenceDetails({ crm }: { crm: CrmPresence }) {
  const touches = [
    crm.calls && plural(crm.calls, "call"),
    crm.emails && plural(crm.emails, "email"),
    crm.meetings && plural(crm.meetings, "meeting"),
    crm.linkedin && plural(crm.linkedin, "LinkedIn message"),
  ].filter(Boolean);
  const lines = [
    crm.match === "same_domain" && "Another company on the same domain is in your HubSpot",
    touches.length ? touches.join(", ") : "Listed, never contacted",
    crm.connects > 0 &&
      `${plural(crm.connects, "connected call")}, last ${formatDate(crm.last_connect_at)}`,
    crm.last_touched_at &&
      `Last touched ${formatDate(crm.last_touched_at)}${crm.last_rep_name ? ` by ${crm.last_rep_name}` : ""}`,
    crm.lead_stage &&
      `Lead: ${crm.lead_stage}${crm.lead_close_reason ? ` — ${crm.lead_close_reason}` : ""}`,
    crm.deals > 0 &&
      `${plural(crm.deals, "deal")}${crm.open_deals ? ` (${formatNumber(crm.open_deals)} open)` : ""}${
        crm.latest_deal_stage ? `, latest ${crm.latest_deal_stage}` : ""
      }`,
  ].filter(Boolean) as string[];
  return (
    <Stack spacing={0.25}>
      {lines.map((line) => (
        <Typography key={line} variant="caption" component="div">
          {line}
        </Typography>
      ))}
    </Stack>
  );
}

/**
 * The tenant's own history with a company in its CRM: the latest deal, a
 * closed-out lead, or when it was last called, emailed, met or messaged. Never
 * another client's work on the same record. Renders nothing without history.
 */
export default function CrmPresenceChip({ crm }: { crm: CrmPresence | null | undefined }) {
  if (!crm) return null;
  const { label, color } = crmPresenceLabel(crm);
  return (
    <Tooltip title={<CrmPresenceDetails crm={crm} />}>
      <Chip
        size="small"
        variant="outlined"
        color={color}
        icon={
          <Box component="span" sx={{ display: "inline-flex", opacity: crm.match === "same_domain" ? 0.5 : 1 }}>
            <HubSpotLogo sx={{ fontSize: 14 }} />
          </Box>
        }
        label={crm.match === "same_domain" ? `Group · ${label}` : label}
      />
    </Tooltip>
  );
}
